import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faFile, faTrash, faRocket } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useAccount } from '../contexts/AccountContext';
import { MMGY_PLAN_ONLY_IO_TYPE, isMmgyPlanOnly, isMmgyPlanOnlyAvailable } from '../utils/mmgyIoType';
import './UploadCreativesModal.css'; // Reuse the same styles

const StartOrchestrationModal = ({ isOpen, onClose, campaign, accountId, user, onOrchestrationStart }) => {
  const [files, setFiles] = useState([]);
  const [uploadedFileData, setUploadedFileData] = useState([]); // Store GCS file data
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const { settings: accountSettings, isLoading: settingsLoading } = useAccountSettings(accountId, user);
  const { accounts } = useAccount();

  // Check if this is an MMGY account (mirrors backend mmgyUtils.js logic)
  const accountName = accounts?.find(a => String(a.id) === String(accountId))?.name || '';
  const isMMGYAccount = accountName && (accountName.toLowerCase() === 'mmgy' || accountName.toLowerCase().startsWith('mmgy-'));
  // MMGY plan-only campaign: the Excel media plan is the IO — no PDF needed,
  // and the orchestration must carry the IO type so the processor is skipped.
  const isPlanOnlyCampaign = Boolean(isMMGYAccount)
    && isMmgyPlanOnly((campaign?.io_type || '').toLowerCase())
    && isMmgyPlanOnlyAvailable([campaign?.destination]);

  // Extract userId from user object (handles both {userId} and {id} formats)
  const effectiveUserId = user?.userId || user?.id;

  // Log settings for debugging
  console.log('🔧 Account Settings:', { accountSettings, settingsLoading, accountId, userId: effectiveUserId, user });

  if (!isOpen) return null;

  // Log campaign data when modal opens
  console.log('🔍 StartOrchestrationModal opened with campaign:', {
    id: campaign?.id,
    name: campaign?.name,
    orchestration_chat_id: campaign?.orchestration_chat_id,
    orchestration_message_id: campaign?.orchestration_message_id,
    hasOrchestrationChatId: !!campaign?.orchestration_chat_id
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      await uploadFilesToGCS(droppedFiles);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      await uploadFilesToGCS(selectedFiles);
    }
    // Reset input
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedFileData(prev => prev.filter((_, i) => i !== index));
  };

  // Helper function to detect platform from filename
  // Only validates if filename contains platform AND "launch" AND "template"
  const detectPlatformFromFilename = (filename) => {
    const lowerName = filename.toLowerCase();

    // Must contain both "launch" and "template"
    if (!lowerName.includes('launch') || !lowerName.includes('template')) {
      return null;
    }

    // Check for platform
    if (lowerName.includes('dv360') || lowerName.includes('dv_360')) {
      return 'dv360';
    }
    if (lowerName.includes('ttd') || lowerName.includes('tradedesk')) {
      return 'ttd';
    }

    return null;
  };

  const uploadFilesToGCS = async (newFiles) => {
    setIsUploading(true);
    try {
      // Add placeholders to files state immediately
      setFiles(prev => [...prev, ...newFiles]);

      const newUploadedFiles = [];

      for (const file of newFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('accountId', accountId);
        formData.append('userId', user?.userId || user?.id);

        const uploadResponse = await api.post(
          getApiUrl('/api/upload'),
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );

        const fileData = {
          originalName: uploadResponse.data.data.originalName,
          gcsPath: uploadResponse.data.data.gcsPath,
          publicUrl: uploadResponse.data.data.publicUrl,
          size: file.size,
          type: file.type
        };

        // Check if validation is enabled and file is Excel
        const isExcel = file.name.match(/\.(xlsx|xls|xlsm)$/i);
        const platform = detectPlatformFromFilename(file.name);

        console.log('🔍 Validation Debug:', {
          filename: file.name,
          isExcel: !!isExcel,
          platform,
          accountSettings,
          validateExcelSetting: accountSettings?.['validate-excel'],
          shouldValidate: isExcel && platform && accountSettings?.['validate-excel'] === 'true'
        });

        const validationEnabled = accountSettings?.['validate-excel'] === 'true';
        const campaignDestination = (campaign?.destination || '').toLowerCase();
        const isMmgyIoCandidate = isMMGYAccount &&
          (campaignDestination === 'dv360' || campaignDestination === 'ttd');

        if (isExcel && validationEnabled && platform) {
          // Existing path: launch-sheet template validation
          try {
            console.log('Running Excel validation for:', file.name);
            setIsValidating(true);

            const validationResponse = await api.post(
              getApiUrl('/api/upload/validate-excel'),
              {
                gcsPath: fileData.gcsPath,
                platform: platform,
                accountId: accountId
              }
            );

            if (validationResponse.data.validation) {
              fileData.validation = validationResponse.data.validation;
              const { valid, errors, warnings } = validationResponse.data.validation;

              if (!valid && errors.length > 0) {
                console.error('Validation errors:', errors);
              } else if (warnings.length > 0) {
                console.warn('Validation warnings:', warnings);
              }
            }
          } catch (validationError) {
            console.error('Validation failed (non-blocking):', validationError);
          } finally {
            setIsValidating(false);
          }
        } else if (isExcel && validationEnabled && isMmgyIoCandidate) {
          // MMGY IO pre-flight validation against the campaign's destination
          const ioType = (campaign?.io_type || 'mmgy').toLowerCase();
          try {
            console.log(`Running MMGY IO validation for: ${file.name} (ioType=${ioType}, platform=${campaignDestination})`);
            setIsValidating(true);

            const validationResponse = await api.post(
              getApiUrl('/api/upload/validate-excel'),
              {
                gcsPath: fileData.gcsPath,
                platform: campaignDestination,
                accountId: accountId,
                ioType: ioType
              }
            );

            if (validationResponse.data.validation) {
              fileData.validation = validationResponse.data.validation;
              const { valid, errors, warnings } = validationResponse.data.validation;

              if (!valid && errors.length > 0) {
                console.error('MMGY IO validation errors:', errors);
              } else if (warnings.length > 0) {
                console.warn('MMGY IO validation warnings:', warnings);
              }
            }
          } catch (validationError) {
            console.error('MMGY IO validation failed (non-blocking):', validationError);
          } finally {
            setIsValidating(false);
          }
        }

        newUploadedFiles.push(fileData);
      }

      setUploadedFileData(prev => [...prev, ...newUploadedFiles]);
      console.log('Files uploaded to GCS:', newUploadedFiles);

    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files: ' + error.message);
      // Remove failed files from state
      setFiles(prev => prev.slice(0, prev.length - newFiles.length));
    } finally {
      setIsUploading(false);
    }
  };

  const validateFiles = () => {
    // Check for required files: PDF and JSON
    const hasPDF = files.some(f =>
      f.name.toLowerCase().endsWith('.pdf') ||
      f.type === 'application/pdf'
    );
    const hasJSON = files.some(f =>
      f.name.toLowerCase().endsWith('.json') ||
      f.type === 'application/json' ||
      f.name.toLowerCase().includes('launch-template') ||
      f.name.toLowerCase().includes('launch_template') ||
      // Also allow Excel files if they look like launch templates
      ((f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls')) &&
       f.name.toLowerCase().includes('launch') &&
       f.name.toLowerCase().includes('template'))
    );

    if (isPlanOnlyCampaign) {
      const hasExcelPlan = files.some(f => /\.(xlsx|xls|xlsm)$/i.test(f.name));
      return hasExcelPlan
        ? { valid: true }
        : { valid: false, error: 'The Excel media plan (.xlsx) is required (Plan only IO type)' };
    }

    if (!hasPDF) {
      return { valid: false, error: 'PDF file is required (Insertion Order)' };
    }

    // MMGY accounts use PDF + Excel files (no JSON launch template required)
    // Excel files don't need "launch"/"template" in the filename for MMGY
    if (isMMGYAccount) {
      const hasExcel = files.some(f => {
        const name = f.name.toLowerCase();
        return name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm');
      });
      if (hasExcel) {
        return { valid: true };
      }
    }

    if (!hasJSON) {
      return { valid: false, error: 'Launch Template file is required (JSON or Excel)' };
    }

    return { valid: true };
  };

  const handleStartOrchestration = async () => {
    const validation = validateFiles();
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setIsUploading(true); // Re-use uploading state for orchestration start
    try {
      // Step 1: Files are already uploaded in uploadedFileData
      
      // Step 2: Get existing chat_id from campaign messages (from upload-creatives)
      // This allows us to reuse the same chat thread
      let existingChatId = null;

      // First try to get from campaign record
      if (campaign.orchestration_chat_id) {
        existingChatId = campaign.orchestration_chat_id;
        console.log('📎 Found chat_id from campaign record:', existingChatId);
      } else {
        // If not in campaign record, fetch from messages endpoint
        console.log('🔍 No chat_id in campaign record, fetching from messages...');
        try {
          const messagesResponse = await api.get(
            getApiUrl(`/api/v2/campaigns/${campaign.id}/messages`),
            {
              params: {
                accountId,
                userId: user?.userId || user?.id
              }
            }
          );

          // Get the first message's chat_id (upload-creatives message)
          if (messagesResponse.data && messagesResponse.data.length > 0) {
            const firstMessage = messagesResponse.data[0];
            existingChatId = firstMessage.chat_id;
            console.log('📎 Found chat_id from first message:', existingChatId, 'Message type:', firstMessage.message_type);
          } else {
            console.log('ℹ️ No messages found for campaign');
          }
        } catch (error) {
          console.error('❌ Failed to fetch messages:', error);
        }
      }

      // Step 3: Start orchestration with uploaded files
      // The orchestration endpoint will handle associating files with the campaign
      const orchestrationPayload = {
        type: 'auto', // Auto-detect workflow type based on files
        platform: campaign.destination,
        campaignId: campaign.id,
        files: uploadedFileData,
        message: `Starting orchestration for ${campaign.name}`,
        metadata: {
          campaignName: campaign.name,
          folderName: campaign.folder_name,
          ...(isPlanOnlyCampaign && { ioType: MMGY_PLAN_ONLY_IO_TYPE })
        }
      };

      // Reuse existing chat_id if available
      if (existingChatId) {
        orchestrationPayload.chatId = existingChatId;
      }

      // Add platform-specific IDs
      if (campaign.advertiser_id) {
        orchestrationPayload.advertiserId = campaign.advertiser_id;
      }
      if (campaign.profile_id) {
        orchestrationPayload.profileId = campaign.profile_id;
      }

      const response = await api.post(
        getApiUrl('/api/v2/orchestration'),
        orchestrationPayload,
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        }
      );

      console.log('✅ Orchestration started successfully:', response.data);

      // Notify parent component with orchestration data
      const orchestrationData = {
        ...response.data,
        chatId: response.data.chatId,
        messageId: response.data.messageId
      };

      console.log('📤 Calling onOrchestrationStart with:', orchestrationData);

      if (onOrchestrationStart) {
        onOrchestrationStart(orchestrationData);
      } else {
        console.error('❌ onOrchestrationStart callback is not defined!');
      }

      console.log('🚪 Closing modal');
      onClose();
    } catch (error) {
      console.error('Failed to start orchestration:', error);
      alert('Failed to start orchestration: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    return faFile;
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Check what files are present
  const hasPDF = files.some(f =>
    f.name.toLowerCase().endsWith('.pdf') ||
    f.type === 'application/pdf'
  );
  const hasJSON = files.some(f =>
    f.name.toLowerCase().endsWith('.json') ||
    f.type === 'application/json' ||
    f.name.toLowerCase().includes('launch-template') ||
    f.name.toLowerCase().includes('launch_template') ||
    ((f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls')) &&
     f.name.toLowerCase().includes('launch') &&
     f.name.toLowerCase().includes('template'))
  );
  const hasExcel = files.some(f => {
    const name = f.name.toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm');
  });
  // MMGY accounts accept any Excel as a valid template (no "launch"/"template" in filename required)
  const hasValidTemplate = hasJSON || (isMMGYAccount && hasExcel);
  const hasCreatives = files.some(f => {
    const name = f.name.toLowerCase();
    return name.endsWith('.zip') || name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.txt');
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faRocket} style={{ marginRight: '8px' }} />
            Start Campaign Orchestration
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="campaign-info">
            <strong>{campaign.name}</strong> - {campaign.destination?.toUpperCase()}
            {campaign.advertiser_id && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Advertiser ID: {campaign.advertiser_id}
              </div>
            )}
          </div>

          <div className="file-requirements" style={{ 
            background: '#f8f9fa', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            fontSize: '13px'
          }}>
            <strong>Required Files:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {!isPlanOnlyCampaign && (
                <li style={{ color: hasPDF ? '#28a745' : '#dc3545' }}>
                  {hasPDF ? '✓' : '✗'} PDF file (Insertion Order)
                </li>
              )}
              <li style={{ color: hasValidTemplate ? '#28a745' : '#dc3545' }}>
                {hasValidTemplate ? '✓' : '✗'} {isMMGYAccount ? 'Excel Media Plan' : 'Launch Template (JSON or Excel)'}
              </li>
              <li style={{ color: hasCreatives ? '#28a745' : '#6c757d' }}>
                {hasCreatives ? '✓' : '○'} Creative files (Optional: ZIP, Excel, or TXT)
              </li>
            </ul>
          </div>

          <div 
            className={`file-drop-zone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <p>Drag and drop files here or click to select</p>
            <small>Accepted: PDF, JSON, ZIP, Excel, TXT</small>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {files.length > 0 && (
            <div className="uploaded-files-list">
              <h4>Selected Files ({files.length})</h4>
              {files.map((file, index) => {
                const fileData = uploadedFileData[index];
                const hasErrors = fileData?.validation && !fileData.validation.valid && fileData.validation.errors?.length > 0;
                const hasWarnings = fileData?.validation && fileData.validation.warnings?.length > 0;

                return (
                  <div key={index} style={{ marginBottom: '15px' }}>
                    <div className="uploaded-file-item">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        className="remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        title="Remove file"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    {hasErrors && (
                      <div style={{
                        marginTop: '8px',
                        marginLeft: '10px',
                        fontSize: '13px',
                        color: '#d32f2f',
                        backgroundColor: '#ffebee',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #d32f2f'
                      }}>
                        {fileData.validation.errors.map((error, i) => (
                          <div key={i} style={{ marginBottom: i < fileData.validation.errors.length - 1 ? '6px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ flexShrink: 0 }}>❌</span>
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {hasWarnings && !hasErrors && (
                      <div style={{
                        marginTop: '8px',
                        marginLeft: '10px',
                        fontSize: '13px',
                        color: '#f57c00',
                        backgroundColor: '#fff3e0',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #f57c00'
                      }}>
                        {fileData.validation.warnings.map((warning, i) => (
                          <div key={i} style={{ marginBottom: i < fileData.validation.warnings.length - 1 ? '6px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ flexShrink: 0 }}>⚠️</span>
                            <span>{warning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleStartOrchestration}
            disabled={
              files.length === 0 ||
              isUploading ||
              isValidating ||
              (!hasPDF && !isPlanOnlyCampaign) ||
              !hasValidTemplate ||
              uploadedFileData.some(f =>
                f.validation && !f.validation.valid && f.validation.errors?.length > 0
              )
            }
            title={isPlanOnlyCampaign
              ? (!hasValidTemplate ? 'The Excel media plan is required' : '')
              : (!hasPDF || !hasValidTemplate ? (isMMGYAccount ? 'PDF and Excel files are required' : 'PDF and Launch Template files are required') : '')}
          >
            {isUploading ? 'Starting Orchestration...' : isValidating ? 'Validating Files...' : 'Start Orchestration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartOrchestrationModal;

